// UAUCAllPanel.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

// --- helpers ---
async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  const data = await res.json().catch(() => null);
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
  return data;
}

function normalizeHazards(hazards) {
  return Array.isArray(hazards)
    ? hazards
        .map((h) => h?.hazards || h?.name || h)
        .filter(Boolean)
        .join(", ")
    : "";
}

function parseDateTimeBoundary(input, endOfDay = false) {
  if (!input) return null;
  const d = new Date(
    /\d{1,2}:\d{2}/.test(input)
      ? input
      : `${input}T${endOfDay ? "23:59:59" : "00:00:00"}`
  );
  return isNaN(d.getTime()) ? null : d;
}

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return acc[key];
    }
    return undefined;
  }, obj);
}


// --- main ---
export default function UAUCAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All UAUC",
}) {
  const [data, setData] = useState([]);
  const [resolvedData, setResolvedData] = useState([]);
  const [query, setQuery] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [ratings, setRatings] = useState({});
  const [adv, setAdv] = useState({
    from: "",
    to: "",
    area: "",
    minRisk: "",
    maxRisk: "",
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const abortRef = useRef(null);
  const navigate = useNavigate();
  const OBSERVER_BOXSHOW = ["userId", "designation", "emailId", "phone"];


  // --- stable helpers ---
  const getOpenStartDate = useCallback(
    (it) => it?.openedAt || it?.date || it?.createdAt,
    []
  );
  const getAssigneeId = useCallback(
    (it) =>
      typeof it?.assignedTo === "object" ? it?.assignedTo?._id : it?.assignedTo,
    []
  );
  const getAssigneeName = useCallback(
    (it) => (typeof it?.assignedTo === "object" ? it?.assignedTo?.name : ""),
    []
  );
  const formatDuration = useCallback((ms) => {
    if (!Number.isFinite(ms)) return "—";
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, []);

  const getSlaHours = useCallback((it) => {
    const rv = it?.riskValue || {};
    const base = Number(rv.alertTimeline) || 0;
    const escalations = Array.isArray(rv.escalationAlert)
      ? rv.escalationAlert.length
      : 0;
    return base * (escalations || 1);
  }, []);

  const getDueAt = useCallback(
    (it) => {
      const opened = getOpenStartDate(it);
      return opened
        ? new Date(new Date(opened).getTime() + getSlaHours(it) * 3600 * 1000)
        : null;
    },
    [getOpenStartDate, getSlaHours]
  );

  // --- fetch data ---
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const url = new URL(apiBase + "/uauc");
        url.searchParams.set("project", projectId);
        const res = await safeFetchJSON(url.toString(), { signal: ac.signal });

        let list = Array.isArray(res) ? res : res?.data || [];
        list = list.filter(
          (it) => String(it?.project?._id || it?.project) === String(projectId)
        );

        setData(list);
        setResolvedData(
          list.filter((it) => /closed|resolved/i.test(it?.status))
        );

        // --- batch ratings ---
        const uniqueIds = [...new Set(list.map(getAssigneeId).filter(Boolean))];
        if (uniqueIds.length) {
          const ratingRes = await Promise.allSettled(
            uniqueIds.map((id) =>
              safeFetchJSON(`${apiBase}/uauc/rating/${id}`, {
                signal: ac.signal,
              })
            )
          );
          const ratingMap = {};
          ratingRes.forEach((r, i) => {
            if (r.status === "fulfilled") ratingMap[uniqueIds[i]] = r.value;
          });
          setRatings(ratingMap);
        }
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId, getAssigneeId]);

  // --- filter + sort ---
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data
      .filter((it) => {
        if (q) {
          const text = [
            it?.observer?.name,
            it?.area,
            normalizeHazards(it?.hazards),
            it?.observation,
          ]
            .join(" ")
            .toLowerCase();
          if (!text.includes(q)) return false;
        }
        if (assignedToId && String(getAssigneeId(it)) !== String(assignedToId))
          return false;

        const opened = getOpenStartDate(it);
        const t = opened ? new Date(opened).getTime() : 0;
        if (adv.from && t < (parseDateTimeBoundary(adv.from)?.getTime() ?? 0))
          return false;
        if (
          adv.to &&
          t > (parseDateTimeBoundary(adv.to, true)?.getTime() ?? Infinity)
        )
          return false;
        if (
          adv.area &&
          !String(it?.area || "")
            .toLowerCase()
            .includes(adv.area.toLowerCase())
        )
          return false;

        const riskVal = Number(it?.riskValue?.value ?? NaN);
        if (
          adv.minRisk &&
          !Number.isNaN(riskVal) &&
          riskVal < Number(adv.minRisk)
        )
          return false;
        if (
          adv.maxRisk &&
          !Number.isNaN(riskVal) &&
          riskVal > Number(adv.maxRisk)
        )
          return false;

        return true;
      })
      .sort(
        (a, b) =>
          new Date(getOpenStartDate(a) || 0) -
          new Date(getOpenStartDate(b) || 0)
      );
  }, [data, query, assignedToId, adv, getOpenStartDate]);

  // --- group by assignee ---
  const groupedByAssignee = useMemo(() => {
    const groups = new Map();
    for (const it of filtered) {
      const key = getAssigneeId(it) || "__unassigned__";
      if (!groups.has(key))
        groups.set(key, {
          id: key,
          name: getAssigneeName(it) || "Unassigned",
          items: [],
        });
      groups.get(key).items.push(it);
    }
    return [...groups.values()];
  }, [filtered, getAssigneeId, getAssigneeName]);

  const openEdit = useCallback(
    (it) => it?._id && navigate(`/edit/uauc/${it._id}`),
    [navigate]
  );

  // --- render ---
  return (
    <div>
      {/* top bar */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
            {filtered.length}
          </span>
        </h4>
        <div className="flex flex-wrap gap-2">
          <input
            className="w-56 rounded-xl border px-3 py-2 text-sm"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value)}
            className="rounded-xl border px-2 py-2 text-sm"
          >
            <option value="">All Assignees</option>
            {[...new Set(data.map(getAssigneeId))].map((id) => {
              const name =
                getAssigneeName(data.find((d) => getAssigneeId(d) === id)) ||
                "Unnamed";
              return (
                <option key={id} value={id}>
                  {name}
                </option>
              );
            })}
          </select>
          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="rounded-xl border px-3 py-2 text-sm"
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {advOpen && (
        <div className="mb-3 grid gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* advanced filter inputs here */}
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : (
        groupedByAssignee.map((section) => (
          <div
            key={section.id}
            className="overflow-hidden rounded-2xl border shadow-sm mb-6"
          >
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between bg-gray-50 px-4 py-3 rounded-t-2xl border-b">
              {/* Left side: name + details */}
              <div className="flex flex-col gap-2">
                <h5 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  👤 {section.name}
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {section.items.length} UAUCs
                  </span>
                </h5>

                {/* 🔹 User details if available */}
                {section.items?.[0]?.assignedTo && (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                    {OBSERVER_BOXSHOW.map((fieldKey) => {
                      const value = section.items[0].assignedTo[fieldKey];
                      if (!value) return null;
                      return (
                        <li
                          key={fieldKey}
                          className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1 shadow-sm border border-gray-200"
                        >
                          <span className="font-medium text-gray-500">
                            {fieldKey}:
                          </span>
                          <span className="text-gray-800 break-words">
                            {value}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Right side: Ratings */}
              {(() => {
                const r = ratings[section.id];
                if (!r)
                  return (
                    <span className="text-xs text-gray-400 self-end">
                      Loading rating…
                    </span>
                  );
                const stars = (c) => (c ? "⭐".repeat(c) : "—");
                return (
                  <div className="flex flex-col items-start sm:items-end text-xs text-gray-700 gap-1 bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    <div className="flex gap-2">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-green-800">
                        Resolved: {r.resolvedCount}
                      </span>
                      <span>Avg: {r.avgClosingTimeDays}d</span>
                      <span>{stars(r.closedStars)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                        Open: {r.openCount}
                      </span>
                      <span>Avg: {r.avgOpenDays}d</span>
                      <span>{stars(r.openStars)}</span>
                    </div>
                    <div className="flex gap-2 font-medium text-indigo-600">
                      🌟 Overall: {r.overallStars} / 5
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Table */}
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <th className="px-4 py-3">Date (Opened)</th>
                  <th className="px-4 py-3">Opened For</th>
                  <th className="px-4 py-3">SLA Due</th>
                  <th className="px-4 py-3">Observer</th>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3">Risk</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {section.items.map((it, idx) => {
                  const opened = getOpenStartDate(it);
                  const due = getDueAt(it);
                  const isClosed = /closed|resolved/i.test(it?.status);
                  let slaBadge;

                  if (due) {
                    if (isClosed) {
                      const closedAt = new Date(
                        it?.closedOn ||
                          it?.closedAt ||
                          it?.resolvedAt ||
                          it?.updatedAt
                      );
                      if (closedAt && opened) {
                        const closedLate = closedAt.getTime() > due.getTime();
                        const duration =
                          closedAt.getTime() - new Date(opened).getTime();
                        slaBadge = (
                          <div className="flex flex-col">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                closedLate
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {closedLate ? "Closed Late" : "Closed On Time"}
                            </span>
                            <span className="text-[11px] text-gray-500 mt-1">
                              Took {formatDuration(duration)}
                            </span>
                          </div>
                        );
                      }
                    } else {
                      const overdue = Date.now() > due.getTime();
                      slaBadge = (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            overdue
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {overdue ? "Overdue" : "On Time"}
                        </span>
                      );
                    }
                  } else slaBadge = "—";

                  return (
                    <tr
                      key={it._id}
                      onClick={() => openEdit(it)}
                      className={`cursor-pointer hover:bg-indigo-50 ${
                        idx % 2 ? "bg-gray-50/30" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3">
                        {opened ? new Date(opened).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {opened
                          ? isClosed
                            ? "—" // hide if closed
                            : formatDuration(
                                Date.now() - new Date(opened).getTime()
                              )
                          : "—"}
                      </td>

                      <td className="px-4 py-3">{slaBadge}</td>
                      <td className="px-4 py-3">{it?.observer?.name || "—"}</td>
                      <td className="px-4 py-3">{it?.area || "—"}</td>
                      <td className="px-4 py-3">
                        {it?.riskValue?.value
                          ? `${it?.riskValue?.value} (${
                              it?.riskValue?.severity || ""
                            })`
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            /closed/i.test(it?.status)
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {it?.status || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
